/**
 * Pure decisions for the phone weekly report (Report.dc.html and its screens).
 * They read the SAME step model the desktop six-step list is built from
 * (api/reportSteps.js#buildSteps) and the same table rows: nothing here
 * invents a state or an action, it only chooses what the phone shows first.
 */

import { STEP_COUNT } from "../api/reportSteps.js";

/** The step the card is about, or null when every step is done (the report is published). */
export function currentStep(built) {
  return (built && built.steps.find((s) => s.state === "current")) || null;
}

/**
 * The next-step card: ONE primary action for the current step. `action` names
 * which existing screen handler the card's button runs.
 *   check -> opens Check the data; confirm -> confirmData; generate -> generate;
 *   approve -> askApprove (the step 4 "Approve draft"); retry -> retryPdf;
 *   publish -> askPublish.
 * `locked` mirrors the desktop button's disabled state (the caller adds its own
 * in-flight rules); `secondary` is the step's own "Open ... in Word / Open PDF" link.
 */
export function nextStepCard(built, { row = null, itemsToFix = 0, period = false } = {}) {
  const step = currentStep(built);
  if (!step) {
    return { done: true, no: STEP_COUNT, total: STEP_COUNT, title: "Published", text: built ? built.summary : "", primary: null, secondary: null, bar: progressBar(built) };
  }
  const base = { done: false, no: step.index + 1, total: STEP_COUNT, title: step.title, text: step.help, secondary: null, bar: progressBar(built), lockNote: step.lockNote || "" };
  if (step.key === "check") {
    const what = period ? "this period" : "this week";
    return {
      ...base,
      text: itemsToFix > 0 ? `${itemsToFix} item${itemsToFix === 1 ? "" : "s"} to fix before you can confirm ${what}. ${step.help}` : step.help,
      primary: { action: "check", label: "Check the data", locked: false },
    };
  }
  if (step.key === "confirm") return { ...base, primary: { action: "confirm", label: step.primary, locked: Boolean(step.primaryLocked) } };
  if (step.key === "generate") return { ...base, primary: { action: "generate", label: step.primary, locked: Boolean(step.primaryLocked) } };
  if (step.key === "review") {
    return {
      ...base,
      primary: step.primary ? { action: "approve", label: step.primary, locked: Boolean(step.primaryLocked) } : null,
      secondary: step.secondary ? { label: step.secondary, url: (row && row.wordUrl) || "" } : null,
    };
  }
  if (step.key === "approve") return { ...base, primary: step.primary ? { action: "retry", label: step.primary, locked: Boolean(step.primaryLocked) } : null };
  return {
    ...base,
    primary: step.primary ? { action: "publish", label: step.primary, locked: Boolean(step.primaryLocked) } : null,
    secondary: step.secondary ? { label: step.secondary, url: (row && row.pdfOpenUrl) || "" } : null,
  };
}

/** The six progress segments: done, now, later. */
export function progressBar(built) {
  return ((built && built.steps) || []).map((s) => ({ label: `${s.title}${s.state === "done" ? " done" : s.state === "current" ? " now" : ""}`, state: s.state }));
}

/**
 * The actions a report card shows: exactly the ones the desktop table row shows
 * (ReportsTable.jsx#ReportRow), in the same cases.
 *   word      Edit in Word (a draft) / Open in Word
 *   approve   a draft
 *   retry     the PDF was not created
 *   site      published, with a project-site link: Open on project site
 *   pdf       otherwise, when a PDF exists: Open PDF
 *   publish   an approved report that is not the one the steps are about
 */
export function reportCardActions(row, { current = false, canPublish = false, canRetry = false } = {}) {
  const actions = [{ kind: "word", label: row.isDraft ? "Edit in Word" : "Open in Word", url: row.wordUrl || "" }];
  if (row.isDraft) actions.push({ kind: "approve", label: "Approve" });
  else if (row.pdfFailed) actions.push({ kind: "retry", label: "Retry PDF", disabled: !canRetry });
  else if (row.isPublished && row.publishedUrl) actions.push({ kind: "site", label: "Open on project site", url: row.publishedUrl });
  else if (row.pdfOpenUrl) actions.push({ kind: "pdf", label: "Open PDF", url: row.pdfOpenUrl });
  if (row.isApproved && !row.pdfFailed && canPublish && !current) actions.push({ kind: "publish", label: "Publish PDF" });
  return actions;
}

/** The report card's meta line: "W41 · 5 – 11 Oct · v4 · generated ...". */
export function reportCardMeta(row) {
  return [row.periodText, `v${row.displayVersion}`, row.generatedText].filter(Boolean).join(" · ");
}

/**
 * Which full-screen view of the draft / PDF / publish flow a project is in, or
 * null. `flow` is the screen's own per-project flow state, `generating` whether
 * a generation is running, `created` whether a generation just finished.
 */
export function flowScreenKind({ generating = false, flow = null, created = false } = {}) {
  if (generating) return "generating";
  if (flow) {
    if (flow.kind === "approving" || flow.kind === "retrying") return "pdf-making";
    if (flow.kind === "pdf_ready") return "pdf-ready";
    if (flow.kind === "publishing") return "publishing";
    if (flow.kind === "published") return "published";
    if (flow.kind === "error") return flow.op === "pdf" ? "pdf-failed" : "error";
  }
  if (created) return "draft-ready";
  return null;
}

/** A key that changes when the flow moves to a new state, so a dismissed screen stays dismissed until then. */
export function flowScreenSignature(kind, { flow = null, generation = null } = {}) {
  if (!kind) return "";
  return [kind, (flow && flow.reportNumber) || "", (flow && flow.at) || (generation && generation.startedAt) || ""].join(":");
}

/** "Week 42 report" / "Custom period report" for the flow screens' title. */
export function reportScreenTitle(header) {
  if (!header) return "Weekly report";
  if (header.custom) return "Custom period report";
  return `${String(header.title || "Week").replace(" · this week", "")} report`;
}
